import React from 'react';
import { Box, Typography } from '@mui/material';
import { QuestionCard } from '../QuestionCard';
import { QuestionLabel } from '../QuestionLabel';
import { StyledTextField } from '../StyledTextField';
import { ToggleOption } from '../ToggleOption';
import { USE_CASE_OPTIONS } from '../../constants/onboardingOptions';
import { FormData } from '../../types/onboarding';
import { colors } from '../../../../theme';

interface UseCaseQuestionProps {
  formData: FormData;
  onChange: (data: Partial<FormData>) => void;
  getGoalsLabel: () => string;
  showPersonalGoals: boolean;
  showBusinessGoals: boolean;
  showBusinessName: boolean;
}

export const UseCaseQuestion: React.FC<UseCaseQuestionProps> = ({
  formData,
  onChange,
  getGoalsLabel,
  showPersonalGoals,
  showBusinessGoals,
  showBusinessName,
}) => {

  return (
    <QuestionCard>
      <QuestionLabel>I want to use filtrd for:</QuestionLabel>
      <Box sx={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '24px' }}>
        {USE_CASE_OPTIONS.map((option) => (
          <ToggleOption
            key={option.value}
            value={option.value}
            label={option.label}
            isSelected={formData.useCase === option.value}
            onClick={() => onChange({ useCase: option.value })}
          />
        ))}
      </Box>

      {/* Goals (conditional on use case selection) */}
      {formData.useCase && (
        <Box sx={{ marginTop: '24px', paddingTop: '24px', borderTop: `1px solid ${colors.slate[200]}` }}>
          <Typography
            sx={{
              fontSize: '1rem',
              fontWeight: 600,
              color: colors.deepNavy,
              marginBottom: '12px',
            }}
          >
            What are your{getGoalsLabel()} goals?
          </Typography>

          {showPersonalGoals && (
            <Box sx={{ marginBottom: showBusinessGoals ? '16px' : 0 }}>
              {showBusinessGoals && (
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: colors.slate[600],
                    marginBottom: '8px',
                  }}
                >
                  Personal goals
                </Typography>
              )}
              <StyledTextField
                value={formData.personalGoals}
                onChange={(value) => onChange({ personalGoals: value })}
                placeholder="e.g., Stay current on AI to inform career decisions, find investment opportunities..."
              />
            </Box>
          )}

          {showBusinessGoals && (
            <Box sx={{ marginBottom: showBusinessName ? '16px' : 0 }}>
              {showPersonalGoals && (
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: colors.slate[600],
                    marginBottom: '8px',
                  }}
                >
                  Business goals
                </Typography>
              )}
              <StyledTextField
                value={formData.businessGoals}
                onChange={(value) => onChange({ businessGoals: value })}
                placeholder="e.g., Track competitor movements, identify market trends, find potential partners..."
              />
            </Box>
          )}

          {/* Business Name Field */}
          {showBusinessName && (
            <Box>
              <Typography
                sx={{
                  fontSize: '1rem',
                  fontWeight: 600,
                  color: colors.deepNavy,
                  marginBottom: '12px',
                }}
              >
                Company URL (optional)
              </Typography>
              <StyledTextField
                value={formData.businessName}
                onChange={(value) => onChange({ businessName: value })}
                placeholder="e.g., Acme Corp or acme.com"
                multiline={false}
              />
            </Box>
          )}
        </Box>
      )}
    </QuestionCard>
  );
};
